import React from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import LandingPage from "./Components/Landingpage";
import PublicJobBoard from "./Components/JobBoard/PublicJobBoard";
import JobDetailsPage from "./Components/JobBoard/JobDetailsPage";
import ForRecruiters from "./Components/LandingPages/ForRecruiters";
import ForJobSeekers from "./Components/LandingPages/ForJobSeekers";
import { SideLayout } from "./Components/SideLayout";
import { savePostLoginRedirect } from "./Components/common/postLoginRedirect";
import Calendar from "./Components/Calendar";
import DashboardPage from "./Components/Dashboard";
import AvailabilityCalendar from "./Components/Calendar/AvailabilityCalendar";
import TermsOfService from "./Components/AuthPanel/TermsOfService";
import PrivacyPolicy from "./Components/AuthPanel/PrivacyPolicy";
import FinishSignupPage from "./Components/FinishSignUp";
import SignInPage from "./Components/AuthPanel/SignInPage";
import SubscriptionForm from "./Components/Subscription";
import { AuthProvider, useUser } from "./context";
import { ROUTES } from "./Components/common";
import "./App.css";
import PricingCards from "./Components/Payment";
import About from "./Components/About";
import CandidatesPanel from "./Components/CandidatesPanel/CandidateDashboard";
import ProfileSetup from "./Components/CandidatesPanel/Profile/ProfileSetup";
import InviteRoute from "./Components/CandidatesPanel/Invite/InviteRoute";
import CandidatePool from 'src/Components/CandidatesPanel/CandidatePool';
import JobsPage from "./Components/RecruitersPanel/Jobs/JobsPage";
import InvitationsPage from "./Components/CandidatesPanel/Invite/InvitationsPage";
import JobApplicationsPage from "./Components/RecruitersPanel/Jobs/JobApplicationsPage";
import { CalendarWrapper } from "./Components/Topbar";
import ApplicantsListPanel from "./Components/RecruitersPanel/applicantsPanel/applicantsListPanel";
import ReportingDashboard from "./Components/RecruitersPanel/Reporting/ReportingDashboard";
import TalentPoolGate from "./Components/TalentPoolGate";
import PaymentHistory from "./Components/Payment/PaymentHistory";



const ProtectedRoute = ({ children }) => {
  const { user } = useUser();
  const location = useLocation();

  if (!user?._id) {
    // Remember where they were headed before bouncing them out to sign in.
    // Links in notification emails point at protected pages, and discarding the
    // destination here is what made those links land on the marketing page.
    savePostLoginRedirect(`${location.pathname}${location.search}`);
    // To the sign-in page, not the marketing home page. Someone arriving from a
    // link in an email wants to sign in, and dropping them on the landing page
    // left them hunting for a button that was not even rendered there. /signin
    // shows the Google and Microsoft buttons directly, and FinishSignUp then
    // returns them to whatever they originally clicked.
    return <Navigate to={ROUTES.SIGN_IN} />;
  }

  if (!user.category) {
    return <Navigate to={ROUTES.FINISH_SIGNUP} />;
  }

  return children;
};

const RoleRoute = ({ children, roles }) => {
  const { user } = useUser();
  if (!roles.includes(user?.category)) {
    return <Navigate to={ROUTES.DASHBOARD} />;
  }
  return children;
};

const App = () => {
  const [jobs, setJobs] = React.useState([]);

  const handleInvite = (job) => {
    // Implement invite logic here (e.g., open modal)
    console.log("Invite clicked for job:", job);
  };

  const handleAddJob = (job) => {
    setJobs(prev => [...prev, { ...job, id: Date.now().toString() }]);
  };

  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path={ROUTES.LANDING} element={<LandingPage />} />
          <Route path={ROUTES.FOR_RECRUITERS} element={<ForRecruiters />} />
          <Route path={ROUTES.FOR_JOB_SEEKERS} element={<ForJobSeekers />} />
          <Route path="/job-board" element={<PublicJobBoard />} />
          <Route path="/jobs/:jobId" element={<JobDetailsPage />} />
          <Route path={ROUTES.SUBSCRIBE_EMAIL} element={<SubscriptionForm />} />
          <Route path={ROUTES.SUBSCRIBE} element={<PricingCards showHeader />} />
          <Route path={ROUTES.TERMS} element={<TermsOfService />} />
          <Route path={ROUTES.POLICY} element={<PrivacyPolicy />} />
          <Route path={ROUTES.ABOUT} element={<About />} />
          <Route path="/invite/:token" element={<InviteRoute />} />

          <Route path={ROUTES.FINISH_SIGNUP} element={<FinishSignupPage />} />
          <Route path={ROUTES.SIGN_IN} element={<SignInPage />} />

          <Route element={<ProtectedRoute><SideLayout /></ProtectedRoute>}>
            {/* Shared */}
            <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />

            {/* Recruiter only */}
            <Route path={ROUTES.PAYMENT_HISTORY} element={<RoleRoute roles={['recruiter']}><PaymentHistory /></RoleRoute>} />
            <Route path="/candidates" element={<RoleRoute roles={['recruiter']}><CandidatePool /></RoleRoute>} />
            <Route path={ROUTES.JOBS} element={<RoleRoute roles={['recruiter']}><TalentPoolGate><JobsPage jobs={jobs} onInvite={handleInvite} onAddJob={handleAddJob} /></TalentPoolGate></RoleRoute>} />
            <Route path="/recruiter/jobs/:jobId/applications" element={<RoleRoute roles={['recruiter']}><TalentPoolGate><JobApplicationsPage /></TalentPoolGate></RoleRoute>} />
            <Route path={ROUTES.REPORTS} element={<RoleRoute roles={['recruiter']}><TalentPoolGate><ReportingDashboard /></TalentPoolGate></RoleRoute>} />

            {/* Manager only */}
            <Route path="/applicants" element={<RoleRoute roles={['manager']}><ApplicantsListPanel /></RoleRoute>} />

            {/* Candidate only */}
            <Route path={ROUTES.CANDIDATE_PROFILE} element={<RoleRoute roles={['candidate']}><ProfileSetup /></RoleRoute>} />
            <Route path={ROUTES.CANDIDATE_DASHBOARD} element={<RoleRoute roles={['candidate']}><CandidatesPanel /></RoleRoute>} />
            <Route path={ROUTES.INVITATIONS} element={<RoleRoute roles={['candidate']}><InvitationsPage /></RoleRoute>} />

            {/* Candidate and Manager */}
            <Route path={ROUTES.CALENDAR} element={<RoleRoute roles={['candidate', 'manager', 'recruiter']}><CalendarWrapper /></RoleRoute>} />
            <Route path={ROUTES.AVAILABILITY} element={<RoleRoute roles={['candidate', 'recruiter']}><AvailabilityCalendar /></RoleRoute>} />

            {/* Recruiter: 1st interview scheduling calendar */}
            <Route path="/recruiter/interview-calendar" element={<RoleRoute roles={['recruiter']}><AvailabilityCalendar /></RoleRoute>} />
          </Route>
        </Routes>
      </AuthProvider>
    </Router>
  );
};

export default App;
